import { Controller, useForm } from 'react-hook-form'
import { useRef, useEffect } from 'react'
import {
  CheckboxGroup,
  CheckboxType,
  RadioGroup,
  Button,
  ButtonColor,
  NotificationType,
  LoaderType,
} from '../../../components'
import {
  aspectRatioOptions,
  videoFormatAddonOptions,
  mapAddonToAspectRatio,
} from '../../../utils/mappers'
import {
  AspectRatio,
  CampaignAddon,
  BrandOnboardingStep,
  Brand,
  Campaign,
  VideoDuration,
} from '../../../gql/types'
import { PricingData, getVideoPricing } from '../../../utils/PricingUtil'
import { AnalyticsEvent, trackEvent } from '../../../utils/analytics'
import { useNotification } from '../../../utils/hooks'
import { useMutation } from '@apollo/client'
import { UPDATE_BRAND, UPDATE_CAMPAIGN } from '../../../gql'
import { Steps } from '../components/Steps'
import { Bullets } from '../components/Bullets'
import { Loader } from '../../../components/Loader'

type EditAddonsFormData = {
  addons?: CampaignAddon[] | []
  addonsSubtitlesMusic?: CampaignAddon | null
  addonsAspectRatios?: CampaignAddon[] | []
  defaultAspectRatio?: AspectRatio | null
}

interface Props {
  brand: Brand
  campaign: Campaign
  pricing: PricingData
}

const SetVideoAspectRatio = ({ brand, campaign, pricing }: Props) => {
  const { addNotification } = useNotification()

  const [updateBrand, { loading: loadingUpdateBrand }] =
    useMutation(UPDATE_BRAND)
  const [updateCampaign, { loading: loadingUpdateCampaign }] =
    useMutation(UPDATE_CAMPAIGN)

  const {
    register,
    handleSubmit,
    control,
    setError,
    formState: { errors },
    setValue,
    watch,
  } = useForm<EditAddonsFormData>({
    defaultValues: {
      addons: campaign?.addons ?? [],
      addonsSubtitlesMusic:
        campaign?.addons?.find(
          (addon) =>
            addon === CampaignAddon.subtitles ||
            addon === CampaignAddon.subtitlesMusic,
        ) ?? null,
      addonsAspectRatios: campaign?.addons
        ? campaign?.addons?.filter(
            (addon) =>
              addon === CampaignAddon.vertical ||
              addon === CampaignAddon.square ||
              addon === CampaignAddon.portrait,
          )
        : [],
      defaultAspectRatio: campaign?.aspectRatio ?? AspectRatio.vertical,
    },
  })

  useEffect(() => {
    trackEvent(AnalyticsEvent.viewBrandsOnboardingSetAddons)
  }, [])

  const videoPricing = getVideoPricing({
    currency: brand.currency,
    videoDuration: campaign.videoDuration ?? VideoDuration.half,
    pricing,
  })

  const addonOptions = videoFormatAddonOptions(
    videoPricing.addons,
    brand.currency,
  )
  const selectedDefault = watch('defaultAspectRatio')
  const currentAddons = watch('addonsAspectRatios') || []
  const prevSelectedDefault = useRef(selectedDefault)

  // Calculate filtered options directly from props without useState
  // This ensures we always show the correct options excluding the selected default
  const filteredAddonOptions = addonOptions.filter(
    (addonOption) =>
      mapAddonToAspectRatio(addonOption.type) !== selectedDefault,
  )

  useEffect(() => {
    if (prevSelectedDefault.current !== selectedDefault) {
      // When landscape is selected, clear all addons as they're not allowed
      if (selectedDefault === AspectRatio.landscape) {
        setValue('addonsAspectRatios', [])
      } else {
        // For other aspect ratios, only remove the one selected as default
        const updatedAddons = currentAddons.filter(
          (addon: CampaignAddon) =>
            mapAddonToAspectRatio(addon) !== selectedDefault,
        )

        // Only update if there's actually something to remove
        if (currentAddons.length !== updatedAddons.length) {
          setValue('addonsAspectRatios', updatedAddons)
        }
      }

      // Update the ref to track the next change
      prevSelectedDefault.current = selectedDefault
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDefault])

  const goBack = async () => {
    try {
      await updateBrand({
        variables: {
          id: brand.id,
          input: {
            onboarding: BrandOnboardingStep.setVideoDuration,
          },
        },
      })
    } catch (error) {
      addNotification(error.message, NotificationType.error)
    }
  }

  const onSubmit = async (data: EditAddonsFormData) => {
    try {
      await Promise.all([
        updateCampaign({
          variables: {
            id: campaign.id,
            input: {
              addons: [
                ...(watch('addonsSubtitlesMusic')
                  ? [watch('addonsSubtitlesMusic')]
                  : []),
                ...(watch('addonsAspectRatios') || []),
              ] as CampaignAddon[] | [],
              aspectRatio: watch('defaultAspectRatio'),
            },
          },
        }),
        updateBrand({
          variables: {
            id: brand.id,
            input: {
              onboarding: BrandOnboardingStep.setVideoPlacements,
            },
          },
        }),
      ])
    } catch (error) {
      console.log('@error SetAddons', error.message)
      if (error.message) {
        setError('addons', { type: 'custom', message: error.message })
        return
      }
    }
  }

  return (
    <div className='min-h-screen flex flex-col pb-14'>
      <div className='flex-grow'>
        <Steps
          brand={brand}
          stepCurrent={BrandOnboardingStep.setVideoAspectRatio}
        />

        <div className='min-h-full flex flex-col justify-center pt-4 px-4 md:mt-8 lg:px-8'>
          <div className='sm:mx-auto sm:w-full sm:max-w-2xl'>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className='md:shadow rounded-t-md sm:overflow-hidden'>
                <div className='py-5 md:bg-white space-y-6 sm:p-6'>
                  <div className='sm:mx-auto'>
                    <h2 className='mt-0 md:text-center text-3xl font-extrabold text-gray-900'>
                      Video format (aspect ratio)
                    </h2>
                    <p className='mt-2 md:text-center text-sm text-gray-600'>
                      Choose your preferred video format. Additional formats can
                      be purchased as add-ons.
                    </p>
                  </div>

                  <div className='grid gap-6'>
                    <Controller
                      name='defaultAspectRatio'
                      control={control}
                      rules={{
                        required: 'Please select a default aspect ratio',
                      }}
                      render={({ field }) => (
                        <RadioGroup
                          label='Default'
                          options={aspectRatioOptions}
                          className='grid grid-cols-2'
                          field={field}
                          error={errors?.defaultAspectRatio?.message}
                        />
                      )}
                    />
                  </div>
                  <div className='grid gap-6'>
                    {selectedDefault !== AspectRatio.landscape && (
                      <CheckboxGroup
                        label='Select additional formats'
                        selected={watch('addonsAspectRatios')}
                        {...register('addonsAspectRatios')}
                        className='grid grid-cols-2'
                        error={errors?.addonsAspectRatios?.message}
                      >
                        {filteredAddonOptions.map(
                          ({ name, type, description, icon }, index) => {
                            return (
                              <CheckboxGroup.CheckboxItem
                                type={CheckboxType.checkbox}
                                key={index}
                                name={type}
                                value={type}
                                title={name}
                                icon={icon}
                                subtitle={description as unknown as string}
                                {...register('addonsAspectRatios')}
                                className='!my-2 !mr-2'
                                inputView={false}
                              />
                            )
                          },
                        )}
                      </CheckboxGroup>
                    )}

                    {selectedDefault === AspectRatio.landscape && (
                      <p className='text-sm leading-5 font-medium text-gray-700'>
                        No additional formats available for landscape videos
                        (16:9). Due to the significant difference between
                        landscape and the other formats, creators cannot easily
                        adapt between them. For both formats, please create
                        separate campaigns.
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className='flex justify-between md:shadow rounded-b-md py-3 md:bg-gray-50 sm:px-6'>
                <Button
                  title='Go back'
                  type='button'
                  colour={ButtonColor.white}
                  loading={loadingUpdateBrand}
                  disabled={loadingUpdateBrand}
                  onClick={goBack}
                />
                <Button
                  title='Continue'
                  type='submit'
                  loading={loadingUpdateBrand || loadingUpdateCampaign}
                  disabled={loadingUpdateBrand || loadingUpdateCampaign}
                />
              </div>
            </form>
          </div>
        </div>
      </div>

      <Bullets stepCurrent={BrandOnboardingStep.setVideoAspectRatio} />
    </div>
  )
}

export { SetVideoAspectRatio }
